import { ChangeEvent, memo, useCallback, useState } from 'react'
import { useRange } from 'react-instantsearch'
import isEqual from 'react-fast-compare'
import { AtBaseInput } from '@curran-catalog/curran-atomic-library'

import { useDebouncedCallback } from '@hooks/use-debounced-callback'

enum RangeKey {
  MAX = 'max',
  MIN = 'min',
}

type Range = {
  [RangeKey.MIN]: number | undefined
  [RangeKey.MAX]: number | undefined
}

const RangeInputComponent = ({ attribute }: { attribute: string }) => {
  const { start, range, refine } = useRange({
    attribute,
  })

  const [rangeState, setRangeState] = useState<Range>({
    min: start[0],
    max: start[1],
  })

  const debouncedHandleInputChange = useDebouncedCallback((key: RangeKey, value?: number) => {
    if (key === RangeKey.MIN) {
      return refine([
        Number.isFinite(value) ? value : undefined,
        Number.isFinite(rangeState.max) ? rangeState.max : undefined,
      ])
    } else if (key === RangeKey.MAX) {
      return refine([
        Number.isFinite(rangeState.min) ? rangeState.min : undefined,
        Number.isFinite(value) ? value : undefined,
      ])
    } else return refine([undefined, undefined])
  }, 600)

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, key: RangeKey) => {
      const value = event.target.value ? Number(event.target.value) : undefined

      setRangeState((prevState) => ({
        ...prevState,
        [key]: value,
      }))

      debouncedHandleInputChange(key, value)
    },
    [debouncedHandleInputChange],
  )

  return (
    <form className="flex items-center justify-center px-4 gap-2 mb-4">
      <div className="w-full">
        <AtBaseInput
          label="Min price $"
          type="number"
          min={range.min}
          max={range.max}
          placeholder={range?.min?.toLocaleString()}
          value={rangeState.min}
          onChange={(event) => handleInputChange(event, RangeKey.MIN)}
        />
      </div>

      <span className="mt-4">{' to '}</span>

      <div className="w-full">
        <AtBaseInput
          label="Max price $"
          type="number"
          min={range.min}
          max={range.max}
          placeholder={range?.max?.toLocaleString()}
          value={rangeState.max}
          onChange={(event) => handleInputChange(event, RangeKey.MAX)}
        />
      </div>
    </form>
  )
}

export const RangeInput = memo(RangeInputComponent, isEqual)
